<template>
  <div class="agent-web-page">
    <div v-if="loading" class="agent-web-status">
      <v-progress-circular indeterminate color="primary" size="32"/>
    </div>

    <div v-else-if="notFound" class="agent-web-status">
      <div class="agent-web-status-icon">
        <Icon name="question-circle" variant="solid" color="muted" size="32"/>
      </div>
      <h1 class="agent-web-status-title">
        404
      </h1>
      <p class="agent-web-status-text">
        Page not found. This agent doesn't exist or the link is no longer valid.
      </p>
    </div>

    <div v-else-if="loadError" class="agent-web-status">
      <div class="agent-web-status-icon">
        <Icon name="warning-circle" variant="solid" color="muted" size="32"/>
      </div>
      <p class="agent-web-status-text">
        This agent is temporarily unavailable. Please try again in a moment.
      </p>
    </div>

    <div v-else class="agent-web-card">
      <div class="agent-web-header">
        <div class="agent-web-avatar">
          <Icon name="agent" variant="solid" color="primary" size="32"/>
        </div>
        <h1 class="agent-web-name">
          {{ agent.name }}
        </h1>
        <p v-if="agent.first_message" class="agent-web-tagline">
          {{ agent.first_message }}
        </p>
      </div>

      <div class="agent-web-call-area">
        <div v-if="!isCallActive" class="web-empty-state">
          <div class="web-empty-icon">
            <Icon name="microphone" variant="solid" color="primary" size="28"/>
          </div>
          <p class="web-empty-title">
            Ready to talk
          </p>
          <p class="web-empty-description">
            Press “Start call” and allow microphone access to speak with {{ agent.name }}.
          </p>
        </div>
        <div v-else class="web-call-active">
          <div class="web-call-indicator">
            <span class="live-dot"/>
            <span class="live-label">Live</span>
          </div>
          <p class="web-call-title">
            Call in progress
          </p>
          <p class="web-call-hint">
            You can speak now. Press “End call” to finish.
          </p>
        </div>
      </div>

      <v-btn
        :color="isCallActive ? 'primary' : undefined"
        class="web-call-btn mank-btn"
        :class="{ 'web-call-btn--active': isCallActive }"
        elevation="0"
        :outlined="!isCallActive"
        block
        :disabled="callLoading"
        :loading="callLoading"
        @click="toggleCall">
        <v-icon
          v-if="isCallActive"
          size="20"
          color="white"
          class="mr-2">
          mdi-stop
        </v-icon>
        <Icon
          v-else
          name="start-call"
          variant="solid"
          color="primary"
          class="mr-2"/>
        {{ isCallActive ? 'End call' : 'Start call' }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import { Room, RoomEvent } from 'livekit-client';
import Icon from '@/components/Icon.vue';
import AgentService from '@/services/AgentService';
import LivekitService from '@/services/LivekitService';
import AnalyticsService from '@/services/AnalyticsService';
import { EVENTS } from '@/constants/analyticsEvents';

export default {
  name: 'AgentWebPage',
  components: { Icon },
  props: {
    id: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loading: true,
      loadError: false,
      notFound: false,
      agent: null,
      isCallActive: false,
      callLoading: false,
      callStartedAt: null,
      roomRef: null,
      audioElements: [],
    };
  },
  async mounted() {
    await this.loadAgent();
  },
  beforeDestroy() {
    this.disconnectRoom();
  },
  methods: {
    agentId() {
      return this.id || this.$route.params.id;
    },
    async loadAgent() {
      const agentId = this.agentId();
      if (!agentId) {
        this.notFound = true;
        this.loading = false;
        return;
      }

      this.loading = true;
      this.loadError = false;
      this.notFound = false;
      try {
        const response = await AgentService.getPublicAgentById(agentId);
        if (response?.success && response.data) {
          this.agent = response.data;
        } else {
          this.notFound = true;
        }
      } catch (error) {
        // 404: ajan yok ya da owner'ı public işaretlememiş → "not found".
        // Diğerleri (429 rate-limit, 503, network) → geçici kullanılamaz durumu.
        if (error?.response?.status === 404) {
          this.notFound = true;
        } else {
          this.loadError = true;
        }
      } finally {
        this.loading = false;
      }
    },
    toggleCall() {
      if (this.isCallActive) {
        this.endCall();
        return;
      }
      this.startCall();
    },
    async startCall() {
      const agentId = this.agentId();
      if (!agentId || !this.agent) return;

      try {
        this.callLoading = true;

        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error('Microphone access is not available. Please open this page over HTTPS.');
        }

        const now = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        const timestamp = `${pad(now.getDate())}${pad(now.getMonth() + 1)}${now.getFullYear()}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
        const roomName = `room-web-${agentId}-${timestamp}`;

        const tokenResp = await LivekitService.getPublicToken(agentId, roomName);
        if (!tokenResp || !tokenResp.data) {
          throw new Error('Could not obtain a LiveKit token.');
        }

        const { token, url } = tokenResp.data;
        const room = new Room();
        this.roomRef = room;

        room.on(RoomEvent.TrackSubscribed, (track) => {
          if (track.kind === 'audio') {
            const element = track.attach();
            document.body.appendChild(element);
            this.audioElements.push(element);
          }
        });

        await room.connect(url, token);
        await room.localParticipant.enableCameraAndMicrophone();

        await LivekitService.dispatchPublicAgent({
          room: roomName,
          phone: 'WEB_TEST',
          agent_id: agentId,
        });

        this.isCallActive = true;
        this.callStartedAt = Date.now();
        AnalyticsService.track(EVENTS.PUBLIC_AGENT_CALL_STARTED, { agent_id: agentId });
      } catch (error) {
        this.disconnectRoom();
        this.isCallActive = false;
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: error.message || 'Could not start the call.',
        });
      } finally {
        this.callLoading = false;
      }
    },
    async endCall() {
      this.callLoading = true;
      await this.disconnectRoom();
      this.isCallActive = false;
      this.callLoading = false;

      AnalyticsService.track(EVENTS.PUBLIC_AGENT_CALL_ENDED, {
        agent_id: this.agentId(),
        duration_ms: this.callStartedAt ? Date.now() - this.callStartedAt : null,
      });
      this.callStartedAt = null;
    },
    async disconnectRoom() {
      if (this.roomRef) {
        try {
          await this.roomRef.disconnect();
        } catch (e) {
          // ignore disconnect errors
        }
        this.roomRef = null;
      }
      this.audioElements.forEach((el) => el.remove());
      this.audioElements = [];
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/colors.scss';
@import '@/styles/variables.scss';

.agent-web-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
}

.agent-web-status {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.agent-web-status-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: $light-surface-2;
  border: 1px solid $light-border;
  display: flex;
  align-items: center;
  justify-content: center;
}

.agent-web-status-title {
  margin: 0;
  font-family: $body-font-family;
  font-size: $h4-size;
  font-weight: 700;
  color: $light-foreground-1;
}

.agent-web-status-text {
  font-family: $body-font-family;
  font-size: $body-2-size;
  color: $light-foreground-3;
  margin: 0;
  text-align: center;
  max-width: 320px;
}

.agent-web-card {
  width: 100%;
  max-width: 440px;
  background: $light-surface-2;
  border: 1px solid $light-border;
  border-radius: $card-border-radius;
  box-shadow: 0 4px 16px rgba(23, 29, 34, 0.06);
  padding: 32px 28px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.agent-web-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
}

.agent-web-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: $light-primary-light;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
}

.agent-web-name {
  margin: 0;
  font-family: $body-font-family;
  font-size: $h5-size;
  font-weight: 700;
  color: $light-foreground-1;
}

.agent-web-tagline {
  margin: 0;
  font-family: $body-font-family;
  font-size: $body-3-size;
  color: $light-foreground-3;
  line-height: 1.5;
}

.agent-web-call-area {
  min-height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: $light-surface-1;
  border: 1px solid $light-border;
  border-radius: $radius-sm;
  padding: 20px;
}

.web-empty-state,
.web-call-active {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
}

.web-empty-icon {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: $light-surface-2;
  border-radius: 50%;
}

.web-empty-title,
.web-call-title {
  margin: 0;
  color: $light-foreground-1;
  font-family: $body-font-family;
  font-size: $body-2-size;
  font-weight: 700;
  line-height: 1.4;
}

.web-empty-description,
.web-call-hint {
  margin: 0;
  color: $light-foreground-3;
  font-family: $body-font-family;
  font-size: $caption-1-size;
  font-weight: 400;
  line-height: 1.6;
  max-width: 300px;
}

.web-call-indicator {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: $light-success-light;
  border-radius: 200px;
  border: 1px solid $light-success-neutral;
}

.live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: $light-success;
  animation: live-pulse 1.5s ease-in-out infinite;
}

.live-label {
  margin: 0;
  font-family: $body-font-family;
  font-size: $caption-1-size;
  font-weight: 600;
  color: $light-success-dark;
}

@keyframes live-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.1); }
}

.web-call-btn {
  height: 44px;
  font-family: $body-font-family;
  font-size: $body-3-size;
  font-weight: 600;
}

.web-call-btn--active {
  :deep(.v-btn__content) {
    color: #fff;
  }
}
</style>
